import React from 'react';
import { Wrapper, Table, HeaderCell, Day, DayOfWeek, Cell, LabelCell, Unit } from './MealHydrationTable.styled';
import { formatMealIntake } from '../../../../utils';
import { EmptyState } from '../../../../components';

interface HealthDataPoint {
  date: string;
  meals?: {
    staple_intake?: number;
    side_intake?: number;
    soup_intake?: number;
    is_not_provided?: boolean;
  };
  hydration_ml?: number;
}

interface MealHydrationTableProps {
  healthData: HealthDataPoint[];
}

/**
 * 食事水分テーブルコンポーネント
 */
export const MealHydrationTable: React.FC<MealHydrationTableProps> = ({ healthData }) => {
  // 主食データが1件でもあるかチェック
  const hasMainIntakeData = healthData.some(
    point => point.meals?.staple_intake != null
  );
  // 副食データが1件でもあるかチェック
  const hasSideIntakeData = healthData.some(
    point => point.meals?.side_intake != null
  );
  // 汁食データが1件でもあるかチェック
  const hasSoupIntakeData = healthData.some(
    point => point.meals?.soup_intake != null
  );
  // 水分データが1件でもあるかチェック
  const hasHydrationData = healthData.some(
    point => point.hydration_ml != null
  );

  // 表示データがない場合はEmptyStateを表示
  if (
    !hasMainIntakeData &&
    !hasSideIntakeData &&
    !hasSoupIntakeData &&
    !hasHydrationData
  ) {
    return (
      <EmptyState
        title="データがありません"
        description="選択された期間に記録がありません"
        icon="📊"
      />
    );
  }

  // 全データを使用（フィルタリングなし）
  const tableData = healthData.map(point => {
    const date = new Date(point.date);
    const day = date.getDate();
    const dayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()];

    return {
      date: point.date,
      day: day,
      dayOfWeek: dayOfWeek,
      mainMeal: formatMealIntake(point.meals?.staple_intake),
      sideMeal: formatMealIntake(point.meals?.side_intake),
      soupMeal: formatMealIntake(point.meals?.soup_intake),
      hydration: point.hydration_ml != null ? `${point.hydration_ml}` : '-',
      mainMealValue: point.meals?.staple_intake,
      sideMealValue: point.meals?.side_intake,
      soupMealValue: point.meals?.soup_intake,
    };
  });

  return (
    <Wrapper>
      <Table>
        <thead>
          <tr>
            <HeaderCell style={{ textAlign: 'left' }}></HeaderCell>
            {tableData.map((data, index) => (
              <HeaderCell key={index}>
                <Day>{data.day}</Day>
                <DayOfWeek>{data.dayOfWeek}</DayOfWeek>
              </HeaderCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {hasMainIntakeData && (
            <tr>
              <LabelCell>主食</LabelCell>
              {tableData.map((data, index) => (
                <Cell
                  key={index}
                  $completed={data.mainMealValue === 10}
                  $partial={data.mainMealValue != null && data.mainMealValue > 0 && data.mainMealValue < 10}
                >
                  {data.mainMeal}
                </Cell>
              ))}
            </tr>
          )}
          {hasSideIntakeData && (
            <tr>
              <LabelCell>副食</LabelCell>
              {tableData.map((data, index) => (
                <Cell
                  key={index}
                  $completed={data.sideMealValue === 10}
                  $partial={data.sideMealValue != null && data.sideMealValue > 0 && data.sideMealValue < 10}
                >
                  {data.sideMeal}
                </Cell>
              ))}
            </tr>
          )}
          {hasSoupIntakeData && (
            <tr>
              <LabelCell>汁食</LabelCell>
              {tableData.map((data, index) => (
                <Cell
                  key={index}
                  $completed={data.soupMealValue === 10}
                  $partial={data.soupMealValue != null && data.soupMealValue > 0 && data.soupMealValue < 10}
                >
                  {data.soupMeal}
                </Cell>
              ))}
            </tr>
          )}
          {hasHydrationData && (
            <tr>
              <LabelCell>水分</LabelCell>
              {tableData.map((data, index) => (
                <Cell
                  key={index}
                  $hydration={data.hydration !== '-'}
                >
                  <div>{data.hydration !== '-' ? `${data.hydration}` : '-'}</div>
                  <Unit>ml</Unit>
                </Cell>
              ))}
            </tr>
          )}
        </tbody>
      </Table>
    </Wrapper>
  );
};

export default MealHydrationTable;
